import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const ServiceCategory = () => {
  const { category } = useParams();
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchServices = useCallback(async (retries = 3) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/${category}`);
      if (response.ok) {
        const data = await response.json();
        setServices(data);
        setLoading(false);
      } else if (retries > 0) {
        setTimeout(() => fetchServices(retries - 1), 2000);
      } else {
        setLoading(false);
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchServices(retries - 1), 2000);
      } else {
        setLoading(false);
      }
    }
  }, [category]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  if (loading) {
    return (
      <div className="loading">
        <p>Loading services...</p>
      </div>
    );
  }

  if (!services) {
    return (
      <div className="section">
        <div className="container">
          <h1>Service category not found</h1>
          <Link to="/services" className="btn-primary">Back to Services</Link>
        </div>
      </div>
    );
  }

  const getCategoryImage = (category) => {
    const images = {
      'facials': 'https://images.pexels.com/photos/3212179/pexels-photo-3212179.jpeg',
      'hair-removal': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/2zjvfr3a_curated-lifestyle-2vwjeqXZevk-unsplash.jpg',
      'lashes-brows': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/c1pxx4gi_amanda-dalbjorn-UbJMy92p8wk-unsplash.jpg',
      'lifestyle': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/t6ommu12_Wellness-Tourism-Why-You-Should-Be-Experiencing-It-e1603999032944.webp',
      'massage': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/pedecleb_ChatGPT%20Image%20Mar%2030%2C%202026%20at%2012_44_43%20PM.png',
      'peels': 'https://customer-assets.emergentagent.com/job_sereno-wellness/artifacts/73ijqcik_chemical-peels-101-a-complete-beginners-guide-1440x810.jpg'
    };
    return images[category] || images['facials'];
  };

  const getServiceImage = (serviceId, category) => {
    // Specific service images
    const serviceImages = {
      'dermaplane-facial': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/1y4xby5n_Dermaplane-Patient-Village-Dermatology.jpg',
      'glo2facial': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/hxxw7lro_glo2facial-fb.png',
      'hydrafacial': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/fdl9jruv_Hydrafacial-1-scaled.jpg',
      'hydrafacial-body': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/98e1vxcd_Hydrabody-Treatment.png',
      'keravive-scalp': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/cp66qtgg_Keravive-Scalp-treatment-1.jpg',
      'sugaring': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/idado0ap_sugaring%20expanded.png',
      'traditional-waxing': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/vssejjek_getty-images-vuRU9fJ9HxI-unsplash.jpg',
      'intimates': 'https://customer-assets.emergentagent.com/job_spa-scheduler-7/artifacts/zjsjpqju_Sugaring_Hair_Removal.jpg',
      'brow-lamination-tint': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/py68fid7_brow-lamination-678x446.jpeg',
      'brow-waxing': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/61x3r0gh_eyebrow-waxing.jpg',
      'lash-lift-tint': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/thholtmh_VB-LASH-LIFTING-IMAGE-1.png',
      'infrared-sauna': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/fonmszcx_suana.jpeg',
      'salt-room': 'https://customer-assets.emergentagent.com/job_sereno-wellness/artifacts/6x5tz1sa_20200820_155916.jpg.webp',
      'compression-therapy': 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/qwxqjbh0_2021-08-19%2B-%2BRapid%2BReboot%2BBoots%2B-%2B012.jpg',
      'led-light-therapy': 'https://customer-assets.emergentagent.com/job_spa-scheduler-7/artifacts/q8hwvv6g_AiyanaAtelier-Branding-TinaLeuFotos-2024-22.jpg',
      'chemical-peel': 'https://customer-assets.emergentagent.com/job_sereno-wellness/artifacts/86wfg8jd_cp.jpg',
      'deep-sea-peel': 'https://customer-assets.emergentagent.com/job_sereno-wellness/artifacts/ex9bxw4o_Deep%2BSea%2BPeel%2Bbanner.png',
      'back-facial': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/e36nneah_ae4db045-7fcf-483c-a4f1-e54fa2183dd6.png',
      'biorepeel': 'https://customer-assets.emergentagent.com/job_spa-scheduler-7/artifacts/opynjpzr_BioRePeel-Image.jpg',
      'microdermabrasion': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/w1m1v27y_a3af82fa-18e6-4da2-bf3d-e7eee90092bf.png',
      'teenage-facial': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/r62ik010_fe6afeb0-2999-44c8-8e11-6c3e2bb663e9.png',
      'customized-massage': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/9r2220k5_b3116957-4453-4502-a9a4-2dc5097bc53e.png',
      'aromatherapy-massage': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/oz4stkvh_ChatGPT%20Image%20Mar%2030%2C%202026%20at%2001_25_47%20PM.png',
      'swedish-massage': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/vcexredd_ChatGPT%20Image%20Mar%2030%2C%202026%20at%2001_48_11%20PM.png',
      'prenatal-massage': 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/h0qgdcot_ChatGPT%20Image%20Mar%2030%2C%202026%20at%2012_48_49%20PM.png'
    };
    
    // Return specific service image if exists, otherwise use category image
    return serviceImages[serviceId] || getCategoryImage(category);
  };

  const getBookingUrl = (serviceId) => {
    const urls = {
      'biorepeel': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A22368321&share=true&pId=2492446',
      'signature-facial': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A22173011&share=true&pId=2492446',
      'sugaring': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=4526724&oiid=sv%3A24636434&share=true&pId=2492446',
      'aromatherapy-massage': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=5115297&oiid=sv%3A24776685&share=true&pId=2492446',
      'customized-massage': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=5115297&oiid=sv%3A24776637&share=true&pId=2492446',
      'dermaplane-facial': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A22173011&share=true&pId=2492446',
      'intimates': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=4526724&oiid=sv%3A24636840&share=true&pId=2492446',
      'led-light-therapy': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A24636064&share=true&pId=2492446',
      'back-facial': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A22365965&share=true&pId=2492446',
      'microdermabrasion': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=4526724&oiid=sv%3A27835828&share=true&pId=2492446',
      'teenage-facial': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&oiid=sv%3A27835786&share=true&pId=2492446',
      'prenatal-massage': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=5115297&oiid=sv%3A26810157&share=true&pId=2492446',
      'swedish-massage': 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&eid=5115297&oiid=sv%3A24776757&share=true&pId=2492446'
    };
    return urls[serviceId] || 'https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&share=true&pId=2492446';
  };

  return (
    <div className="service-category-page">
      <SEOHead 
        title={`${services.name} | Professional Spa Treatments | Sereno Spa`}
        description={`Professional ${services.name.toLowerCase()} treatments at Sereno Spa in Livingston Manor, NY. Book your appointment today.`}
        canonical={`https://serenodayspa.com/services/${category}`}
        ogTitle={`${services.name} - Sereno Spa`}
        ogDescription={`Discover our ${services.name.toLowerCase()} services. Professional spa treatments tailored to your needs in Livingston Manor, NY.`}
      />
      <section className="category-hero">
        <div className="category-hero-image">
          <img 
            src={getCategoryImage(category)} 
            alt={services.name}
            className="hero-bg"
            style={category === 'massage' ? { objectPosition: 'center 30%' } : {}}
          />
          <div className="category-hero-overlay">
            <div className="container">
              <h1>{services.name}</h1>
              <p>Professional treatments tailored to your needs</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="services-detailed-grid">
            {services.services.map(service => (
              <div key={service.id} className="service-detail-card">
                <div className="service-detail-content">
                  <h3>{service.name}</h3>
                  <p className="service-description">{service.description}</p>
                  <div className="service-details">
                    <div className="service-duration">
                      <span className="label">Duration:</span>
                      <span className="value">{service.id === 'customized-massage' ? '60 - 120 minutes' : `${service.duration} minutes`}</span>
                    </div>
                  </div>
                  <div className="service-actions">
                    <a 
                      href={getBookingUrl(service.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                    >
                      Book This Service
                    </a>
                  </div>
                </div>
                <div className="service-image-container">
                  <img 
                    src={getServiceImage(service.id, category)} 
                    alt={service.name}
                    className="service-detail-image"
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="category-navigation">
            <Link to="/services" className="btn-secondary">
              ← Back to All Services
            </Link>
            <a href="https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&share=true&pId=2492446" target="_blank" rel="noopener noreferrer" className="btn-primary">
              Book Any Service
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceCategory;